function initFacetSwatchContrast(scope = document) {
  scope.querySelectorAll('.collection-facets .checkbox .product-option__swatch--needs-contrast').forEach((swatch) => {
    if (swatch.dataset.contrastInit === 'true') return;

    const background = getComputedStyle(swatch).getPropertyValue('--swatch--background').trim();
    const urlMatch = background.match(/url\(["']?([^"')]+)["']?\)/);

    if (!urlMatch) {
      swatch.classList.remove('product-option__swatch--needs-contrast');
      swatch.classList.add('product-option__swatch--on-light');
      swatch.dataset.contrastInit = 'true';
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;

      canvas.width = width;
      canvas.height = height;
      context.drawImage(image, 0, 0, width, height);

      const pixel = context.getImageData(Math.floor(width / 2), Math.floor(height / 2), 1, 1).data;
      const luma = 0.299 * pixel[0] + 0.587 * pixel[1] + 0.114 * pixel[2];

      swatch.classList.remove('product-option__swatch--needs-contrast');
      swatch.classList.add(luma < 140 ? 'product-option__swatch--on-dark' : 'product-option__swatch--on-light');
      swatch.dataset.contrastInit = 'true';
    };
    image.onerror = () => {
      swatch.classList.remove('product-option__swatch--needs-contrast');
      swatch.classList.add('product-option__swatch--on-light');
      swatch.dataset.contrastInit = 'true';
    };
    image.src = urlMatch[1];
  });
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    document.getElementById('ProductGridContainer').querySelector('.collection-grid-container').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');
    const sourceGrid = sourceContainer?.querySelector('.js-collection-grid');
    const targetGrid = targetContainer?.querySelector('.js-collection-grid');

    if (sourceGrid && targetGrid) {
      targetGrid.innerHTML = sourceGrid.innerHTML;
    } else if (sourceContainer && targetContainer) {
      targetContainer.innerHTML = sourceContainer.innerHTML;
    }

    targetContainer
      ?.querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    FacetFiltersForm.scrollToProductGrid();
  }

  static scrollToProductGrid() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const top = productGrid.getBoundingClientRect().top + window.scrollY - headerHeight - 16;

    window.scrollTo({ top: Math.max(0, top) });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static getFilterElements(scope = document) {
    return scope.querySelectorAll('facet-filters-form .js-filter');
  }

  static renderFilters(html, event) {
    const sidebarFilterList = document.querySelector('.collection-grid__sidebar-filter');
    const sidebarFilterScrollTop = sidebarFilterList?.scrollTop ?? 0;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = FacetFiltersForm.getFilterElements(parsedHTML);
    const facetDetailsElementsFromDom = FacetFiltersForm.getFilterElements();

    const fetchedIds = new Set(Array.from(facetDetailsElementsFromFetch).map((element) => element.id));

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!fetchedIds.has(currentElement.id)) {
        currentElement.remove();
      }
    });

    const activeJsFilter = event?.target?.closest?.('.js-filter');
    const isTextInput = event?.target?.matches?.('input[type="text"], input[type="number"]');

    Array.from(facetDetailsElementsFromFetch).forEach((elementToRender) => {
      if (isTextInput && activeJsFilter?.id === elementToRender.id) return;

      FacetFiltersForm.getFilterElements().forEach((currentElement) => {
        if (currentElement.id === elementToRender.id) {
          currentElement.innerHTML = elementToRender.innerHTML;
        }
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderResetFilters(parsedHTML);
    // Ambos acaban de reescribirse con el HTML del servidor, que desconoce el
    // filtro de precio en modo cliente: el componente repone su chip y la
    // visibilidad del botón de restablecer.
    document.querySelectorAll('price-range').forEach((element) => element.syncActiveFacetsUI?.());

    if (sidebarFilterList) {
      sidebarFilterList.scrollTop = sidebarFilterScrollTop;
    }

    initFacetSwatchContrast();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderResetFilters(html) {
    const resetSelectors = [
      '.collection__facets--sidebar .facets__reset-filters',
      '.collection-facets .facets__sidebar-header .facets__reset-filters',
    ];

    resetSelectors.forEach((selector) => {
      const resetFromFetch = html.querySelector(selector);
      const resetInDom = document.querySelector(selector);
      if (!resetInDom) return;

      if (resetFromFetch) {
        resetInDom.outerHTML = resetFromFetch.outerHTML;
      } else {
        resetInDom.hidden = true;
      }
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  static getFormsForSubmit(event) {
    const targetForm = event?.target?.closest?.('form');

    if (targetForm) {
      return [targetForm];
    }

    const visibleForms = [...document.querySelectorAll('facet-filters-form form')].filter((form) => {
      const facetForm = form.closest('facet-filters-form');
      return facetForm && facetForm.offsetParent !== null && form.querySelector('.js-filter');
    });

    return visibleForms.length > 0 ? visibleForms : [...document.querySelectorAll('facet-filters-form form')];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortParam = new URLSearchParams(window.location.search).get('sort_by');
    const params = new URLSearchParams();

    if (sortParam) params.set('sort_by', sortParam);

    FacetFiltersForm.getFormsForSubmit(event).forEach((form) => {
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        if (input.checked) params.append(input.name, input.value);
      });

      form.querySelectorAll('input:not([type="checkbox"])').forEach((input) => {
        // Los campos de visualización del filtro de precio no llevan `name`: sólo se envían
        // los inputs ocultos con el valor en la moneda de la tienda.
        if (!input.name) return;

        if (input.value !== '') {
          const min = input.getAttribute('min');
          const max = input.getAttribute('max');
          const isDefault =
            (input.name.includes('gte') && input.value === min) ||
            (input.name.includes('lte') && input.value === max);

          if (!isDefault) {
            params.set(input.name, input.value);
          }
        }
      });
    });

    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener('DOMContentLoaded', () => initFacetSwatchContrast());

// Formato por defecto: el de la moneda de la tienda antes de que la app de divisas
// reescriba los `.money`.
const PRICE_DEFAULT_FORMAT = {
  symbol: '',
  symbolPosition: 'prefix',
  decimals: 2,
  decimalSeparator: ',',
  groupSeparator: '.',
};

const PRICE_NUMERIC_PATTERN = /\d[\d.,\u00A0\u202F ']*\d|\d/;

// Lee una cantidad ya formateada ("€549,99", "2,708.00 kr", "88.600 Ft", "CHF 228.00")
// y deduce su valor numérico junto al formato usado, sin asumir ninguna moneda.
function parseMoneyString(text) {
  if (!text) return null;

  const clean = String(text)
    .replace(/&nbsp;/g, ' ')
    .replace(/[\u00A0\u202F]/g, ' ')
    .trim();
  const match = clean.match(PRICE_NUMERIC_PATTERN);
  if (!match) return null;

  const numeric = match[0];
  const before = clean.slice(0, match.index).trim();
  const after = clean.slice(match.index + numeric.length).trim();

  const lastSeparator = Math.max(numeric.lastIndexOf(','), numeric.lastIndexOf('.'));
  let decimalSeparator = '';
  let decimals = 0;

  if (lastSeparator !== -1) {
    const fraction = numeric.slice(lastSeparator + 1).replace(/\D/g, '');
    // Tres dígitos detrás significan separador de miles (88.600 Ft), no decimales.
    if (fraction.length > 0 && fraction.length < 3) {
      decimalSeparator = numeric[lastSeparator];
      decimals = fraction.length;
    }
  }

  const integerPart = decimalSeparator ? numeric.slice(0, lastSeparator) : numeric;
  const groupSeparators = integerPart.match(/[.,\s']/g);
  const groupSeparator = groupSeparators
    ? groupSeparators[groupSeparators.length - 1]
    : decimalSeparator === ',' ? '.' : ',';

  const digits = decimalSeparator
    ? `${integerPart.replace(/\D/g, '')}.${numeric.slice(lastSeparator + 1).replace(/\D/g, '')}`
    : integerPart.replace(/\D/g, '');

  const value = parseFloat(digits);
  if (Number.isNaN(value)) return null;

  return {
    value,
    format: {
      symbol: before || after,
      symbolPosition: !before && after ? 'suffix' : 'prefix',
      decimals,
      decimalSeparator: decimalSeparator || ',',
      groupSeparator,
    },
  };
}

function formatMoneyNumber(value, format) {
  const safe = Number.isFinite(value) ? Math.max(value, 0) : 0;
  const [integerPart, fraction] = safe.toFixed(format.decimals).split('.');
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupSeparator);
  return fraction ? `${grouped}${format.decimalSeparator}${fraction}` : grouped;
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.init();
  }

  init() {
    this.minInput = this.querySelector('[data-price-input="min"]');
    this.maxInput = this.querySelector('[data-price-input="max"]');
    this.minValueInput = this.querySelector('[data-price-value="min"]');
    this.maxValueInput = this.querySelector('[data-price-value="max"]');
    this.probe = this.querySelector('[data-price-probe]');
    this.currencyLabels = this.querySelectorAll('[data-price-currency]');
    this.slider = this.querySelector('.facets__price-slider');
    this.track = this.querySelector('.facets__price-slider-track');
    this.range = this.querySelector('.facets__price-slider-range');
    this.thumbMin = this.querySelector('.facets__price-slider-thumb--min');
    this.thumbMax = this.querySelector('.facets__price-slider-thumb--max');

    if (!this.minInput || !this.maxInput || !this.minValueInput || !this.maxValueInput || !this.slider) return;

    // Modo cliente: Shopify no sirve el filtro de precio en mercados cuya divisa
    // difiere de la de la tienda (e ignora `filter.v.price.*`), así que el rango se
    // calcula desde las tarjetas cargadas y el filtrado se hace ocultándolas aquí.
    this.clientMode = this.dataset.clientFilter === 'true';
    this.filterFrame = null;
    this.activeThumb = null;
    this.rate = 1;
    this.moneyFormat = { ...PRICE_DEFAULT_FORMAT };

    this.rangeMin = this.parseNumber(this.dataset.rangeMin) ?? 0;
    this.rangeMax = this.parseNumber(this.dataset.rangeMax) ?? 0;

    if (this.clientMode) {
      // El servidor nunca devuelve los valores elegidos (no conoce el filtro):
      // la URL es la única fuente de verdad tras cada re-render.
      this.hydrateFromUrl();
      this.readConversion();
      this.rangeMax = this.computeClientRange();
    }

    this.step = this.getStep();

    this.currentMin =
      this.parseNumber(this.minValueInput.value) ?? this.parseNumber(this.dataset.currentMin) ?? this.rangeMin;
    this.currentMax =
      this.parseNumber(this.maxValueInput.value) ?? this.parseNumber(this.dataset.currentMax) ?? this.rangeMax;

    this.normalizeValues();
    this.bindEvents();
    this.readConversion();
    this.updateSliderUI(false);
    this.updateDisplayInputs();
    this.setMinAndMaxValues();
    this.observeConversion();

    if (this.clientMode) this.setupClientFiltering();
  }

  disconnectedCallback() {
    this.probeObserver?.disconnect();
    this.gridObserver?.disconnect();
    if (this.filterFrame) {
      cancelAnimationFrame(this.filterFrame);
      this.filterFrame = null;
    }
  }

  hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const min = this.parseNumber(params.get(this.minValueInput.name));
    const max = this.parseNumber(params.get(this.maxValueInput.name));

    this.minValueInput.value = min === null ? '' : min.toFixed(2);
    this.maxValueInput.value = max === null ? '' : max.toFixed(2);
  }

  getGridCards() {
    const grid = document.getElementById('product-grid');
    return grid ? Array.from(grid.children) : [];
  }

  // Precio de venta de una tarjeta, en la moneda base del componente. En oferta se
  // toma el precio rebajado (el <ins>), no el tachado.
  getCardPrice(card) {
    const priceElement = card.querySelector('.price__sale ins') || card.querySelector('.price__regular');
    if (!priceElement) return null;

    const parsed = parseMoneyString(priceElement.textContent);
    return parsed ? this.toShop(parsed.value) : null;
  }

  computeClientRange() {
    let max = 0;

    this.getGridCards().forEach((card) => {
      const value = this.getCardPrice(card);
      if (value !== null && value > max) max = value;
    });

    if (max <= 0) {
      // Sin tarjetas con precio (p. ej. colección vacía): rango mínimo utilizable.
      max = Math.max(this.parseNumber(this.maxValueInput.value) ?? 0, 1);
    }

    return Math.ceil(max);
  }

  // Coalescido por frame: puede dispararse en ráfaga (una mutación del grid por
  // tarjeta durante un re-render, o un pointermove por píxel al arrastrar el
  // slider) y basta con un único escaneo del grid por frame de pintado.
  applyClientFilter() {
    if (!this.clientMode || this.filterFrame) return;

    this.filterFrame = requestAnimationFrame(() => {
      this.filterFrame = null;
      this.applyClientFilterNow();
    });
  }

  applyClientFilterNow() {
    const min = this.parseNumber(this.minValueInput.value);
    const max = this.parseNumber(this.maxValueInput.value);
    const active = min !== null || max !== null;
    let visible = 0;

    this.getGridCards().forEach((card) => {
      const value = this.getCardPrice(card);
      if (value === null) return; // bloques de texto u otros elementos del grid

      const hidden = active && ((min !== null && value < min) || (max !== null && value > max));
      card.classList.toggle('facets__price-hidden', hidden);
      if (!hidden) visible += 1;
    });

    this.updateFilteredCount(active, visible);
    this.syncActiveFacetsUI(active);
  }

  isClientFilterActive() {
    return this.parseNumber(this.minValueInput.value) !== null || this.parseNumber(this.maxValueInput.value) !== null;
  }

  // El chip de faceta activa y el botón de restablecer los renderiza el servidor
  // desde `collection.filters`, que en modo cliente no incluye el precio: al
  // filtrar por precio no aparecía ninguno de los dos (sí con el resto de
  // filtros, que Shopify sí conoce). Se reproducen aquí.
  syncActiveFacetsUI(active) {
    // Puede llegar de `renderFilters` sobre cualquier price-range de la página,
    // incluido uno cuyo `init()` se detuvo por marca incompleta.
    if (!this.clientMode || !this.minValueInput) return;

    const isActive = active ?? this.isClientFilterActive();
    this.renderPriceChip(isActive);
    this.toggleResetButtons(isActive);
  }

  // URL actual sin los parámetros de precio: es la que abre el chip al cerrarse.
  buildRemoveUrl() {
    const params = new URLSearchParams(window.location.search);
    params.delete(this.minValueInput.name);
    params.delete(this.maxValueInput.name);

    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  formatChipValue(value) {
    const number = this.formatDisplay(value);
    const symbol = this.moneyFormat.symbol;

    if (!symbol) return number;
    return this.moneyFormat.symbolPosition === 'suffix' ? `${number} ${symbol}` : `${symbol}${number}`;
  }

  renderPriceChip(active) {
    const container = document.querySelector('.active-facets');
    if (!container) return;

    // Un único chip: el cajón móvil y la barra lateral montan cada uno su
    // price-range y comparten este contenedor, así que cada pasada retira el
    // chip anterior antes de poner el suyo.
    container.querySelectorAll('[data-price-chip-active]').forEach((element) => element.remove());
    if (!active) return;

    const template = this.querySelector('template[data-price-chip]');
    if (!template) return;

    const chip = template.content.firstElementChild.cloneNode(true);
    chip.setAttribute('data-price-chip-active', '');
    chip.querySelector('[data-price-chip-label]').textContent = `${this.formatChipValue(
      this.currentMin
    )} - ${this.formatChipValue(this.currentMax)}`;
    chip.querySelector('a').href = this.buildRemoveUrl();

    // Con muchas facetas activas los chips viajan dentro de un carrusel.
    const slider = container.querySelector('.swiper-wrapper');
    if (!slider) {
      container.appendChild(chip);
      return;
    }

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.setAttribute('data-price-chip-active', '');
    slide.appendChild(chip);
    slider.appendChild(slide);
  }

  toggleResetButtons(active) {
    document.querySelectorAll('.facets__reset-filters').forEach((element) => {
      if (active) {
        if (!element.hidden) return;
        element.hidden = false;
        // Marcado para poder devolverlo a su estado de servidor al limpiar: si
        // hay otras facetas activas, el botón ya venía visible y no es nuestro.
        element.dataset.priceForced = 'true';
      } else if (element.dataset.priceForced === 'true') {
        element.hidden = true;
        delete element.dataset.priceForced;
      }
    });
  }

  updateFilteredCount(active, visible) {
    // Sin filtro activo el texto del servidor ya es correcto; con filtro se
    // sustituye la cifra por las tarjetas visibles.
    if (!active) return;

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const element = document.getElementById(id);
      if (!element) return;

      const updated = element.textContent.replace(/\d+/, String(visible));
      if (element.textContent !== updated) element.textContent = updated;
    });
  }

  setupClientFiltering() {
    this.applyClientFilter();

    const container = document.getElementById('ProductGridContainer');
    if (!container || typeof MutationObserver === 'undefined') return;

    // Reaplica el filtro cuando el grid cambia: re-render por facetas, "cargar
    // más" o scroll infinito. Solo observa childList, así ocultar tarjetas
    // (cambio de atributo) no re-dispara el observer.
    this.gridObserver = new MutationObserver(() => this.applyClientFilter());
    this.gridObserver.observe(container, { childList: true, subtree: true });
  }

  // La app de divisas reescribe el contenido del `.money` de la sonda. Comparando el
  // resultado con el valor original obtenemos el tipo de cambio, el símbolo y el formato
  // que se están mostrando realmente en la página.
  readConversion() {
    if (!this.probe) return;

    const base = this.parseNumber(this.probe.dataset.priceProbeBase);
    const parsed = parseMoneyString(this.probe.textContent);
    if (!parsed || !base || base <= 0) return;

    this.rate = parsed.value / base;
    if (!Number.isFinite(this.rate) || this.rate <= 0) this.rate = 1;
    this.moneyFormat = parsed.format;

    this.applyCurrencyLabels();
  }

  observeConversion() {
    if (!this.probe || typeof MutationObserver === 'undefined') return;

    this.probeObserver = new MutationObserver(() => {
      this.readConversion();
      this.updateDisplayInputs();
    });
    this.probeObserver.observe(this.probe, { childList: true, subtree: true, characterData: true });
  }

  applyCurrencyLabels() {
    const symbol = this.moneyFormat.symbol;
    const isSuffix = this.moneyFormat.symbolPosition === 'suffix';
    // En los mercados cuya divisa no es EUR el hueco calculado por longitud
    // queda desajustado para los dos campos del filtro: se fija a mano —
    // 3.3rem para los símbolos de 1-2 letras ("zł", "kr", "Kč", "Ft", "lei")
    // y 5rem para CHF, cuyo símbolo de 3 letras va en prefijo. La divisa
    // activa la expone el body en data-currency (theme.liquid), el mismo
    // gancho que usa el CSS para ensanchar el sidebar con estas divisas.
    const currency = document.body.dataset.currency;
    let fixedSpace = null;
    if (currency === 'CHF') {
      fixedSpace = '5rem';
    } else if (['PLN', 'SEK', 'CZK', 'DKK', 'HUF', 'RON'].includes(currency)) {
      fixedSpace = '3.3rem';
    }

    this.currencyLabels.forEach((label) => {
      if (symbol) label.textContent = symbol;

      const row = label.closest('.facets__price-row');
      if (!row) return;

      row.classList.toggle('facets__price-row--suffix', isSuffix);
      // El hueco reservado para el símbolo depende de su longitud ("€" frente a "CHF").
      row.style.setProperty('--price-symbol-space', fixedSpace || `${Math.max(symbol.length, 1) * 0.95 + 2.4}rem`);
    });
  }

  toDisplay(value) {
    return value * this.rate;
  }

  toShop(value) {
    return this.rate ? value / this.rate : value;
  }

  getStep() {
    const span = this.rangeMax - this.rangeMin;
    if (span <= 100) return 1;
    if (span <= 1000) return 5;
    return 10;
  }

  parseNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    const num = parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(num) ? null : num;
  }

  // Los campos visibles muestran la moneda activa, así que hay que interpretarlos con su
  // formato y devolver el importe en la moneda de la tienda.
  parseDisplayInput(input) {
    const parsed = parseMoneyString(input.value);
    if (!parsed) return null;
    return this.toShop(parsed.value);
  }

  formatDisplay(value) {
    return formatMoneyNumber(this.toDisplay(value), this.moneyFormat);
  }

  normalizeValues() {
    this.currentMin = this.clamp(this.currentMin, this.rangeMin, this.rangeMax);
    this.currentMax = this.clamp(this.currentMax, this.rangeMin, this.rangeMax);

    if (this.currentMin > this.currentMax) {
      this.currentMin = this.currentMax;
    }

    const minGap = this.step;
    if (this.currentMax - this.currentMin < minGap) {
      if (this.activeThumb === this.thumbMax) {
        this.currentMax = Math.min(this.rangeMax, this.currentMin + minGap);
      } else {
        this.currentMin = Math.max(this.rangeMin, this.currentMax - minGap);
      }
    }
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  valueToPercent(value) {
    if (this.rangeMax <= this.rangeMin) return 0;
    return ((value - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
  }

  percentToValue(percent) {
    const raw = this.rangeMin + (percent / 100) * (this.rangeMax - this.rangeMin);
    return this.clamp(Math.round(raw / this.step) * this.step, this.rangeMin, this.rangeMax);
  }

  bindEvents() {
    this.minInput.addEventListener('change', this.onRangeChange.bind(this));
    this.maxInput.addEventListener('change', this.onRangeChange.bind(this));
    // El formulario se autoenvía con debounce al escribir, así que hay que mantener los
    // inputs ocultos al día en cada pulsación y no sólo al perder el foco.
    this.minInput.addEventListener('input', this.onRangeInput.bind(this));
    this.maxInput.addEventListener('input', this.onRangeInput.bind(this));
    this.minInput.addEventListener('keydown', this.onKeyDown.bind(this));
    this.maxInput.addEventListener('keydown', this.onKeyDown.bind(this));

    if (!this.thumbMin || !this.thumbMax || !this.track) return;

    this.thumbMin.addEventListener('pointerdown', (event) => this.onThumbPointerDown(event, this.thumbMin));
    this.thumbMax.addEventListener('pointerdown', (event) => this.onThumbPointerDown(event, this.thumbMax));
    this.track.addEventListener('pointerdown', this.onTrackPointerDown.bind(this));

    this.thumbMin.addEventListener('keydown', (event) => this.onThumbKeyDown(event, 'min'));
    this.thumbMax.addEventListener('keydown', (event) => this.onThumbKeyDown(event, 'max'));
  }

  onThumbPointerDown(event, thumb) {
    event.preventDefault();
    this.activeThumb = thumb;
    thumb.classList.add('is-dragging');
    thumb.setPointerCapture(event.pointerId);
    thumb.addEventListener('pointermove', this.onPointerMove);
    thumb.addEventListener('pointerup', this.onPointerUp);
    thumb.addEventListener('pointercancel', this.onPointerUp);
  }

  onTrackPointerDown(event) {
    if (event.target.closest('.facets__price-slider-thumb')) return;

    const percent = this.getPercentFromPointer(event);
    const value = this.percentToValue(percent);
    const distanceToMin = Math.abs(value - this.currentMin);
    const distanceToMax = Math.abs(value - this.currentMax);

    this.activeThumb = distanceToMin <= distanceToMax ? this.thumbMin : this.thumbMax;
    this.setValueFromPercent(percent);
    this.syncInputs(false);
    this.onThumbPointerDown(event, this.activeThumb);
  }

  onPointerMove(event) {
    if (!this.activeThumb) return;
    this.setValueFromPercent(this.getPercentFromPointer(event));
    this.syncInputs(false);
  }

  onPointerUp(event) {
    if (!this.activeThumb) return;

    this.activeThumb.classList.remove('is-dragging');
    this.activeThumb.releasePointerCapture(event.pointerId);
    this.activeThumb.removeEventListener('pointermove', this.onPointerMove);
    this.activeThumb.removeEventListener('pointerup', this.onPointerUp);
    this.activeThumb.removeEventListener('pointercancel', this.onPointerUp);

    this.syncInputs(true);
    this.activeThumb = null;
  }

  getPercentFromPointer(event) {
    const rect = this.track.getBoundingClientRect();
    const isRtl = document.documentElement.dir === 'rtl';
    const position = isRtl ? rect.right - event.clientX : event.clientX - rect.left;
    const percent = (position / rect.width) * 100;
    return this.clamp(percent, 0, 100);
  }

  setValueFromPercent(percent) {
    const value = this.percentToValue(percent);

    if (this.activeThumb === this.thumbMin) {
      this.currentMin = Math.min(value, this.currentMax - this.step);
      this.currentMin = this.clamp(this.currentMin, this.rangeMin, this.rangeMax);
    } else {
      this.currentMax = Math.max(value, this.currentMin + this.step);
      this.currentMax = this.clamp(this.currentMax, this.rangeMin, this.rangeMax);
    }

    this.updateSliderUI(false);
    this.setMinAndMaxValues();
  }

  // Los inputs ocultos son los que viajan a Shopify: siempre en la moneda de la tienda y
  // con punto decimal, que es el único formato que acepta `filter.v.price.*`.
  syncInputs(shouldSubmit) {
    const minValue = this.currentMin <= this.rangeMin ? '' : (Math.round(this.currentMin * 100) / 100).toFixed(2);
    const maxValue = this.currentMax >= this.rangeMax ? '' : (Math.round(this.currentMax * 100) / 100).toFixed(2);

    if (this.minValueInput.value !== minValue) this.minValueInput.value = minValue;
    if (this.maxValueInput.value !== maxValue) this.maxValueInput.value = maxValue;

    this.updateDisplayInputs();
    this.applyClientFilter();

    if (shouldSubmit) {
      // Se emite desde el campo visible (type="text") para que `renderFilters` conserve
      // este bloque de filtro y no pierda el foco durante la actualización.
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  // Refleja en los campos visibles el importe convertido a la moneda activa.
  updateDisplayInputs(skipInput) {
    this.minInput.placeholder = this.formatDisplay(this.rangeMin);
    this.maxInput.placeholder = this.formatDisplay(this.rangeMax);

    const minValue = this.minValueInput.value ? this.formatDisplay(this.currentMin) : '';
    const maxValue = this.maxValueInput.value ? this.formatDisplay(this.currentMax) : '';

    if (skipInput !== this.minInput && this.minInput.value !== minValue) this.minInput.value = minValue;
    if (skipInput !== this.maxInput && this.maxInput.value !== maxValue) this.maxInput.value = maxValue;
  }

  updateSliderUI(updateInputs) {
    const minPercent = this.valueToPercent(this.currentMin);
    const maxPercent = this.valueToPercent(this.currentMax);

    if (this.thumbMin) this.thumbMin.style.insetInlineStart = `${minPercent}%`;
    if (this.thumbMax) this.thumbMax.style.insetInlineStart = `${maxPercent}%`;

    if (this.range) {
      this.range.style.insetInlineStart = `${minPercent}%`;
      this.range.style.width = `${Math.max(maxPercent - minPercent, 0)}%`;
    }

    if (updateInputs) {
      this.syncInputs(false);
    }
  }

  // Al escribir sólo se sincronizan los valores ocultos, sin reformatear el campo que el
  // usuario está editando.
  onRangeInput(event) {
    const input = event.currentTarget;

    this.currentMin = this.clamp(this.parseDisplayInput(this.minInput) ?? this.rangeMin, this.rangeMin, this.rangeMax);
    this.currentMax = this.clamp(this.parseDisplayInput(this.maxInput) ?? this.rangeMax, this.rangeMin, this.rangeMax);

    const minValue = this.currentMin <= this.rangeMin ? '' : (Math.round(this.currentMin * 100) / 100).toFixed(2);
    const maxValue = this.currentMax >= this.rangeMax ? '' : (Math.round(this.currentMax * 100) / 100).toFixed(2);

    this.minValueInput.value = minValue;
    this.maxValueInput.value = maxValue;

    this.updateSliderUI(false);
    this.setMinAndMaxValues();
    this.updateDisplayInputs(input);
    this.applyClientFilter();
  }

  onRangeChange() {
    this.currentMin = this.parseDisplayInput(this.minInput) ?? this.rangeMin;
    this.currentMax = this.parseDisplayInput(this.maxInput) ?? this.rangeMax;
    this.normalizeValues();
    this.updateSliderUI(false);
    this.syncInputs(false);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape', 'Home', 'End'];
    // Se admite el espacio porque algunas monedas lo usan como separador de miles.
    if (!allowedKeys.includes(event.key) && !/[0-9.,\s]/.test(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
      const facetFiltersForm = this.closest('facet-filters-form');
      if (facetFiltersForm) {
        facetFiltersForm.onSubmitHandler({ preventDefault: () => {}, target: event.target });
      }
    }
  }

  onThumbKeyDown(event, type) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    this.activeThumb = type === 'min' ? this.thumbMin : this.thumbMax;

    if (event.key === 'Home') {
      if (type === 'min') this.currentMin = this.rangeMin;
      else this.currentMax = this.rangeMin + this.step;
    } else if (event.key === 'End') {
      if (type === 'max') this.currentMax = this.rangeMax;
      else this.currentMin = this.rangeMax - this.step;
    } else {
      const direction = ['ArrowRight', 'ArrowUp'].includes(event.key) ? 1 : -1;
      if (type === 'min') {
        this.currentMin = this.clamp(this.currentMin + direction * this.step, this.rangeMin, this.currentMax - this.step);
      } else {
        this.currentMax = this.clamp(this.currentMax + direction * this.step, this.currentMin + this.step, this.rangeMax);
      }
    }

    this.normalizeValues();
    this.updateSliderUI(false);
    this.syncInputs(true);
    this.setMinAndMaxValues();
    this.activeThumb = null;
  }

  setMinAndMaxValues() {
    this.minValueInput.setAttribute(
      'data-max',
      this.maxValueInput.value || this.maxValueInput.getAttribute('max') || ''
    );
    this.maxValueInput.setAttribute(
      'data-min',
      this.minValueInput.value || this.minValueInput.getAttribute('min') || ''
    );
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

